// API Service for Backend Integration

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://minbayte.prootech-cloud.com';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('authToken');
  }

  // Set authentication token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  // Get authentication token
  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  // Generic fetch method
  async fetch(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken();

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let data;

      try {
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          // Try to parse as JSON if it looks like JSON
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || 'حدث خطأ في الطلب' };
          }
        }
      } catch (parseError) {
        // If JSON parsing fails, try to get text
        try {
          const text = await response.text();
          // Try to parse as JSON
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || 'حدث خطأ في الطلب' };
          }
        } catch (textError) {
          data = { message: 'حدث خطأ في الطلب' };
        }
      }

      if (!response.ok) {
        // Auto-logout on 401 (expired/invalid token)
        if (response.status === 401 && !endpoint.includes('/login')) {
          this.setToken(null);
          localStorage.removeItem('user');
          window.location.href = '/';
          return;
        }
        const errorMessage = data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      // If it's already our custom error, re-throw it
      if (error.status) {
        throw error;
      }
      // Network or other errors
      console.error('API Error:', error);

      // Check if it's a network error
      if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        throw new Error('لا يمكن الاتصال بالخادم. تأكد من أن الباك اند يعمل على ' + this.baseURL);
      }

      throw new Error(error.message || 'حدث خطأ في الاتصال بالخادم');
    }
  }

  // Authentication
  async login(credentials) {
    try {
      const response = await this.fetch('/users/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
      // Backend returns accessToken
      if (response.accessToken) {
        this.setToken(response.accessToken);
        return { success: true, accessToken: response.accessToken, user: response.user };
      }
      return { success: false, message: 'فشل تسجيل الدخول - لم يتم استلام التوكن' };
    } catch (error) {
      console.error('Login error:', error);
      // Handle 401 specifically
      if (error.status === 401) {
        return { success: false, message: 'البريد الإلكتروني أو كلمة المرور غير صحيحة' };
      }
      return { success: false, message: error.message || 'حدث خطأ أثناء تسجيل الدخول' };
    }
  }

  async register(userData) {
    try {
      const response = await this.fetch('/users/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      return response;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  }

  async logout() {
    try {
      // Backend might not have logout endpoint, so just clear local storage
      this.setToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear anyway
      this.setToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  // Dashboard APIs
  async getAdminHomePage() {
    return this.fetch('/dashboard/adminHomePage');
  }

  async getAdminSummary() {
    return this.fetch('/dashboard/admin/summary');
  }

  async getGrowthReport(months = 6) {
    return this.fetch(`/dashboard/reports/growth?months=${months}`);
  }

  async getSalesReport(months = 6) {
    return this.fetch(`/dashboard/reports/sales?months=${months}`);
  }

  // Notifications
  async getNotifications(page = 1, limit = 20) {
    return this.fetch(`/dashboard/notifications?page=${page}&limit=${limit}`);
  }

  async sendNotification(title, message, target) {
    return this.fetch('/dashboard/notifications/send', {
      method: 'POST',
      body: JSON.stringify({ title, message, target }),
    });
  }

  // Settings
  async getSettings() {
    return this.fetch('/dashboard/settings');
  }

  async updateSettings(settings) {
    return this.fetch('/dashboard/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getAccountManagement(userType, page = 1, limit = 10) {
    return this.fetch(`/dashboard/accountManagement?userType=${userType}&page=${page}&limit=${limit}`);
  }

  async getContent(type, page = 1, limit = 24) {
    return this.fetch(`/dashboard/getContent?type=${type}&page=${page}&limit=${limit}`);
  }

  async approveRestaurant(id) {
    return this.fetch(`/dashboard/restaurants/${id}/approve`, {
      method: 'POST',
    });
  }

  async rejectRestaurant(id, reason) {
    return this.fetch(`/dashboard/restaurants/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Categories
  async getCategories(type) {
    const url = type ? `/categories?type=${type}` : '/categories';
    return this.fetch(url);
  }

  async getCategoryById(id) {
    return this.fetch(`/categories/${id}`);
  }

  async createCategory(categoryData, file = null) {
    // Backend expects multipart/form-data
    const formData = new FormData();
    formData.append('name', categoryData.name);
    formData.append('description', categoryData.description || '');
    formData.append('type', categoryData.type || 'restaurant');

    if (file) {
      formData.append('file', file);
    }

    const url = `${this.baseURL}/categories`;
    const token = this.getToken();

    const config = {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    };

    try {
      const response = await fetch(url, config);

      const contentType = response.headers.get('content-type');
      let data;

      try {
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          data = { message: text || 'حدث خطأ في الطلب' };
        }
      } catch (parseError) {
        const text = await response.text().catch(() => 'حدث خطأ في الطلب');
        data = { message: text || 'حدث خطأ في الطلب' };
      }

      if (!response.ok) {
        const errorMessage = data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      if (error.status) {
        throw error;
      }
      console.error('API Error:', error);
      throw new Error(error.message || 'حدث خطأ في الاتصال بالخادم');
    }
  }

  async updateCategory(id, categoryData) {
    return this.fetch(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteCategory(id) {
    return this.fetch(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // User Management
  async getUserById(id) {
    return this.fetch(`/users/${id}`);
  }

  async updateUser(id, userData) {
    return this.fetch(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id) {
    return this.fetch(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Restaurant Management
  async getRestaurantById(id, type = 'restaurant') {
    return this.fetch(`/restaurants/${id}?type=${type}`);
  }

  async updateRestaurant(id, restaurantData, type = 'restaurant') {
    // Backend expects multipart/form-data, but we can send JSON for simple updates
    // Convert to FormData if needed, otherwise send as JSON
    const formData = new FormData();

    // Add all fields to FormData
    Object.keys(restaurantData).forEach(key => {
      const value = restaurantData[key];

      // Special handling for isActive - always append even if false
      if (key === 'isActive') {
        if (value === true || value === 'true' || value === 1 || value === '1') {
          formData.append(key, 'true');
          console.log('✅ Appending isActive: true');
        } else {
          // Explicitly handle false case - this is critical!
          formData.append(key, 'false');
          console.log('✅ Appending isActive: false');
        }
      } else if (value !== undefined && value !== null) {
        // Convert boolean to string for FormData (backend expects string 'true'/'false')
        if (typeof value === 'boolean') {
          formData.append(key, value ? 'true' : 'false');
        } else if (typeof value === 'string' && (value === 'true' || value === 'false')) {
          // Already a string boolean, keep it
          formData.append(key, value);
        } else {
          formData.append(key, value);
        }
      }
    });

    console.log('FormData contents:', {
      isActive: restaurantData.isActive,
      isActiveType: typeof restaurantData.isActive,
      allData: restaurantData
    });

    console.log('Sending restaurant update:', { id, type, data: restaurantData });

    const url = `${this.baseURL}/restaurants/${id}?type=${type}`;
    const token = this.getToken();

    const config = {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    };

    try {
      const response = await fetch(url, config);

      const contentType = response.headers.get('content-type');
      let data;

      try {
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || 'حدث خطأ في الطلب' };
          }
        }
      } catch (parseError) {
        const text = await response.text().catch(() => 'حدث خطأ في الطلب');
        try {
          data = JSON.parse(text);
        } catch {
          data = { message: text || 'حدث خطأ في الطلب' };
        }
      }

      if (!response.ok) {
        const errorMessage = data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      if (error.status) {
        throw error;
      }
      console.error('API Error:', error);
      throw new Error(error.message || 'حدث خطأ في الاتصال بالخادم');
    }
  }

  async deleteRestaurant(id, type = 'restaurant') {
    return this.fetch(`/restaurants/${id}?type=${type}`, {
      method: 'DELETE',
    });
  }

  // Content Management
  async deletePost(id) {
    return this.fetch(`/posts/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteStory(id) {
    return this.fetch(`/stories/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteVideo(id) {
    return this.fetch(`/videos/${id}`, {
      method: 'DELETE',
    });
  }

  // Countries
  async getCountries() {
    return this.fetch('/countries');
  }

  async getCountryById(id) {
    return this.fetch(`/countries/${id}`);
  }

  async createCountry(countryData, imageFile = null, logoFile = null) {
    const formData = new FormData();
    formData.append('name', countryData.name);

    if (imageFile) {
      formData.append('image', imageFile);
    }
    if (logoFile) {
      formData.append('logoImage', logoFile);
    }

    const url = `${this.baseURL}/countries`;
    const token = this.getToken();

    const config = {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    };

    try {
      const response = await fetch(url, config);

      const contentType = response.headers.get('content-type');
      let data;

      try {
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          throw new Error(text || 'حدث خطأ أثناء إنشاء البلد');
        }
      } catch (parseError) {
        throw new Error('حدث خطأ أثناء معالجة الاستجابة');
      }

      if (!response.ok) {
        throw new Error(data.message || `خطأ ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Create country error:', error);
      throw error;
    }
  }

  async updateCountry(id, countryData, imageFile = null, logoFile = null) {
    const formData = new FormData();
    formData.append('name', countryData.name);

    if (imageFile) {
      formData.append('image', imageFile);
    }
    if (logoFile) {
      formData.append('logoImage', logoFile);
    }

    const url = `${this.baseURL}/countries/${id}`;
    const token = this.getToken();

    const config = {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    };

    try {
      const response = await fetch(url, config);

      const contentType = response.headers.get('content-type');
      let data;

      try {
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          const text = await response.text();
          throw new Error(text || 'حدث خطأ أثناء تحديث البلد');
        }
      } catch (parseError) {
        throw new Error('حدث خطأ أثناء معالجة الاستجابة');
      }

      if (!response.ok) {
        throw new Error(data.message || `خطأ ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Update country error:', error);
      throw error;
    }
  }

  async deleteCountry(id) {
    return this.fetch(`/countries/${id}`, {
      method: 'DELETE',
    });
  }

  // Orders Management
  async getOrders(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return this.fetch(`/dashboard/orders?${params.toString()}`);
  }

  async getOrderById(id) {
    return this.fetch(`/dashboard/orders/${id}`);
  }

  async updateOrderStatus(id, status) {
    return this.fetch(`/dashboard/orders/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async getOrderStatistics(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return this.fetch(`/dashboard/orders/statistics?${params.toString()}`);
  }

  // Restaurant Management
  async getRestaurantDetails(id) {
    return this.fetch(`/dashboard/restaurants/${id}/details`);
  }

  async getRestaurantMeals(id, page = 1, limit = 20) {
    return this.fetch(`/dashboard/restaurants/${id}/meals?page=${page}&limit=${limit}`);
  }

  // Payment Management
  async getPaymentTransactions(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return this.fetch(`/dashboard/payments/transactions?${params.toString()}`);
  }

  async getPaymentStatistics(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return this.fetch(`/dashboard/payments/statistics?${params.toString()}`);
  }

  // Load Test Management
  async startLoadTest(config) {
    return this.fetch('/dashboard/load-test/start', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async stopLoadTest() {
    return this.fetch('/dashboard/load-test/stop', {
      method: 'POST',
    });
  }

  async getLoadTestStats() {
    return this.fetch('/dashboard/load-test/stats');
  }

  async getLoadTestResults() {
    return this.fetch('/dashboard/load-test/results');
  }

  // Advanced Reports
  async getTopRestaurants(metric = 'orders', limit = 10, fromDate, toDate) {
    const params = new URLSearchParams({ metric, limit: limit.toString() });
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    return this.fetch(`/dashboard/reports/top-restaurants?${params.toString()}`);
  }

  async getTopMeals(limit = 10, fromDate, toDate) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    return this.fetch(`/dashboard/reports/top-meals?${params.toString()}`);
  }

  async getPeakHours(fromDate, toDate) {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    return this.fetch(`/dashboard/reports/peak-hours?${params.toString()}`);
  }

  async getTopAreas(limit = 10, fromDate, toDate) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    return this.fetch(`/dashboard/reports/top-areas?${params.toString()}`);
  }

  async getActiveUsers(limit = 10, fromDate, toDate) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    return this.fetch(`/dashboard/reports/active-users?${params.toString()}`);
  }

  // Activity Log
  async getActivityLogs(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return this.fetch(`/dashboard/activity?${params.toString()}`);
  }

  async getActivityStatistics(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    return this.fetch(`/dashboard/activity/statistics?${params.toString()}`);
  }

  // WhatsApp Management
  async getWhatsAppStatus() {
    return this.fetch('/whatsapp/status');
  }

  async getWhatsAppQRCode() {
    return this.fetch('/whatsapp/qr-code');
  }

  async getWhatsAppInfo() {
    return this.fetch('/whatsapp/info');
  }

  async resetWhatsAppConnection() {
    return this.fetch('/whatsapp/reset', {
      method: 'POST',
    });
  }

  // Payment Methods Management
  async getAllPaymentMethods() {
    return this.fetch('/payment-methods/admin');
  }

  async updatePaymentMethod(id, data) {
    return this.fetch(`/payment-methods/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async togglePaymentMethod(id) {
    return this.fetch(`/payment-methods/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  async createPaymentMethod(data) {
    return this.fetch('/payment-methods', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export default new ApiService();




